import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { Smartphone } from "lucide-react";
import { userData } from "@/store";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const REMINDER_REQUIRED_KEY = "mobile-verification-reminder-required";

const MobileVerificationReminder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useRecoilValue(userData);
  const [open, setOpen] = useState(false);

  const reminderKey = useMemo(() => {
    if (!userInfo?.ID) return "";
    return `mobile-verification-reminder-dismissed-${userInfo.ID}`;
  }, [userInfo?.ID]);

  const shouldShowReminder =
    Boolean(userInfo?.ID) &&
    sessionStorage.getItem(REMINDER_REQUIRED_KEY) === "true" &&
    location.pathname === "/dashboard";

  useEffect(() => {
    if (!shouldShowReminder || !reminderKey) {
      setOpen(false);
      return;
    }

    setOpen(sessionStorage.getItem(reminderKey) !== "true");
  }, [reminderKey, shouldShowReminder]);

  const dismiss = () => {
    if (reminderKey) {
      sessionStorage.setItem(reminderKey, "true");
    }
    setOpen(false);
  };

  const handleVerifyClick = () => {
    dismiss();
    navigate("/myProfile");
  };

  if (!shouldShowReminder) return null;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && dismiss()}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-sm rounded-xl p-0 overflow-hidden">
        <DialogHeader className="items-center text-center gap-3 px-6 pt-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            <Smartphone className="h-6 w-6" />
          </div>
          <DialogTitle className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Mobile Number Not Verified
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
            Please verify your mobile number to keep your account information up
            to date.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2 px-6 pb-6 pt-4 sm:gap-2">
          <Button type="button" variant="outline" onClick={dismiss}>
            Later
          </Button>
          <Button type="button" onClick={handleVerifyClick}>
            Please Verify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MobileVerificationReminder;